"use client";

import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { useParaAccount } from "@/hooks/use-appkit-account";
import { toast } from "sonner";
import { useWertOnramp } from "@/hooks/use-wert-onramp";
import { ChainId } from "@lifi/sdk";
import { CreditCard, Wallet, ExternalLinkIcon, Loader2 } from "lucide-react";
import { useCreditCardBuyStore } from "@/lib/zustand";
import { usePortfolioStore } from "../wallet/mobile-portfolio-toggle";

interface OnRampOption {
  name: string;
  symbol: string;
  chainId: number;
  network: string;
  commodity: string;
  logoUrl?: string;
}

const onRampOptions: OnRampOption[] = [
  {
    name: "Solana",
    symbol: "SOL",
    chainId: ChainId.SOL,
    network: "solana",
    commodity: "SOL",
    logoUrl: "https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png",
  },
  {
    name: "Ethereum",
    symbol: "ETH",
    chainId: 1,
    network: "ethereum",
    commodity: "ETH",
    logoUrl: "https://tokens.1inch.io/0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee.png",
  },
  {
    name: "BNB Chain",
    symbol: "BNB",
    chainId: 56,
    network: "bsc",
    commodity: "BNB",
    logoUrl: "https://tokens.1inch.io/0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c.png",
  },
  {
    name: "Base",
    symbol: "ETH",
    chainId: 8453,
    network: "base",
    commodity: "ETH",
    logoUrl:
      "https://raw.githubusercontent.com/base-org/brand-kit/001c0e9b40a67799ebe0418671ac4e02a0c683ce/logo/in-product/Base_Network_Logo.svg",
  },
];

export function QuickOnRamp({ compact = false }: { compact?: boolean }) {
  const { address } = useParaAccount();
  const { isWertWidgetOpen, setSelectedOnRampChainId } = useCreditCardBuyStore();
  const { openWertOnRamp } = useWertOnramp();
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [isAmountDialogOpen, setIsAmountDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState<OnRampOption | null>(null);
  const [usdAmount, setUsdAmount] = useState<string>("5");
  const [isExpanded, setIsExpanded] = useState(false);
  const { setIsOpen: setPortfolioOpen } = usePortfolioStore();

  // Monitor for widget closure to reset loading state
  useEffect(() => {
    if (!isWertWidgetOpen && isLoading) {
      setIsLoading(null);
    }

    // Auto close dialog when widget is open
    if (isWertWidgetOpen && isAmountDialogOpen) {
      setIsAmountDialogOpen(false);
      setPortfolioOpen(false); // Close portfolio sidebar too
    }
  }, [isWertWidgetOpen, isLoading, isAmountDialogOpen, setPortfolioOpen]);

  const openAmountDialog = (option: OnRampOption) => {
    setSelectedOnRampChainId(option.chainId);

    if (!address) {
      toast.error("Please connect your wallet first");
      return;
    }

    if (isWertWidgetOpen) {
      toast.error("A payment widget is already open");
      return;
    }

    setSelectedOption(option);
    setUsdAmount("100");
    setIsAmountDialogOpen(true);
  };

  const handleConfirmOnRamp = async (paymentMethod?: "apple-pay" | "google-pay") => {
    if (!selectedOption) return;

    const parsedUsdAmount = parseFloat(usdAmount);
    if (isNaN(parsedUsdAmount) || parsedUsdAmount <= 0) {
      toast.error("Please enter a valid USD amount greater than 0");
      return;
    }

    if (parsedUsdAmount < 5) {
      toast.error("Minimum Card Purchase Of $5 USD");
      return;
    }

    const option = selectedOption;

    await openWertOnRamp({
      chainId: option.chainId,
      network: option.network,
      commodity: option.commodity,
      usdAmount: parsedUsdAmount,
      paymentMethod,
      onStart: () => {
        setIsLoading(option.symbol);
      },
      onFinally: () => {
        setIsLoading(null);
        setIsAmountDialogOpen(false);
        setSelectedOption(null);
      },
    });
  };

  return (
    <>
      {compact ? (
        <div className="w-full">
          <div className="flex items-center gap-2 text-white text-[18px] font-bold leading-none">
            <span className="text-[18px]" role="img" aria-label="Credit card">💳</span>
            <span>Buy Crypto Instantly</span>
          </div>
          <p className="mt-1.5 text-[13px] text-[#888]">
            Purchase crypto with your Debit/Credit Card, Apple Pay, or Google Pay
          </p>

          <div className="mt-4 flex flex-wrap gap-2 text-white">
            {onRampOptions.map((option) => (
              <Button
                key={option.chainId}
                variant="outline"
                className="flex-1 basis-[calc(50%-4px)] min-w-[130px] sm:min-w-0 flex flex-col items-center gap-1.5 h-auto py-2 px-2 border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.03)] text-[#aaa] hover:bg-[rgba(255,255,255,0.05)] rounded-[10px]"
                onClick={() => {
                  openAmountDialog(option);
                }}
                disabled={!address || isWertWidgetOpen || isLoading === option.symbol}
              >
                {option.logoUrl ? (
                  <img src={option.logoUrl} alt={option.name} className="w-6 h-6 rounded-full" />
                ) : (
                  <Wallet className="w-5 h-5" />
                )}
                <span className="font-medium text-xs">{option.name === "BNB Chain" ? "BNB" : option.name}</span>
              </Button>
            ))}
          </div>

          {!address && <p className="text-xs text-[#a0a0a0] mt-2 text-center">Connect your wallet to buy crypto</p>}
        </div>
      ) : (

        <Card className="w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-white">
              <CreditCard className="h-5 w-5" />
              Buy Crypto with Card
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-white">
              {onRampOptions.map((option) => (
                <Button
                  key={option.chainId}
                  variant="outline"
                  className="flex flex-col items-center gap-2 h-auto py-4 hover:bg-gray-100 dark:hover:bg-gray-800 text-white"
                  onClick={() => {
                    openAmountDialog(option);
                  }}
                  disabled={!address || isWertWidgetOpen || isLoading === option.symbol}
                >
                  {option.logoUrl ? (
                    <img src={option.logoUrl} alt={option.name} className="w-8 h-8 rounded-full" />
                  ) : (
                    <Wallet className="w-6 h-6" />
                  )}
                  <div className="flex flex-col items-center">
                    <span className="font-semibold">{option.symbol}</span>
                    <span className="text-xs text-white">{option.name}</span>
                  </div>
                  {isLoading === option.symbol && <span className="text-xs text-white">Loading...</span>}
                </Button>
              ))}
            </div>
            {!address && <p className="text-sm text-white mt-4 text-center">Connect your wallet to buy crypto</p>}
          </CardContent>
        </Card>
      )}
      <Dialog open={isAmountDialogOpen} onOpenChange={setIsAmountDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-primary">Buy {selectedOption?.symbol || ""} with Debit/Credit Card</DialogTitle>
            <DialogClose />
          </DialogHeader>
          <div className="space-y-4 flex flex-col w-full mt-4">
            <div className="relative w-full">
              <Input value={usdAmount} onChange={(e) => setUsdAmount(e.target.value)} placeholder="Enter USD amount..." type="number" min={0} step="0.01" className="pl-8 placeholder:text-foreground/50 bg-foreground/10 border border-border outline-none text-foreground text-[20px] sm:text-[30px] font-medium h-[72px] rounded-2xl" />
              <p className="text-foreground/50 text-[20px] absolute top-1/2 transform -translate-y-1/2 left-4 -translate-x-1/2">$</p>
            </div>
            <div className="text-xs flex flex-col items-start gap-1 text-muted-foreground">Minimum Card Purchase Of $5 USD. Must live in an area that is supported by Wert.<a href="https://widget.wert.io/service-availability" target="_blank" rel="noreferrer" className="underline text-blue-500 hover:text-blue-600 flex items-center gap-1">View Supported Locations <ExternalLinkIcon className="w-2 h-2" /></a></div>
            <p className="text-xs text-yellow-400 text-center mt-2">
              ⚠️ If your transaction is declined, contact your bank and ask them to allow purchases from Wert.
            </p>
            <Button type="button" className="w-full mt-5" onClick={() => handleConfirmOnRamp()} disabled={isWertWidgetOpen || !selectedOption || (selectedOption && isLoading === selectedOption.symbol)}>{selectedOption && isLoading === selectedOption.symbol ? <Loader2 className="w-4 h-4 animate-spin" /> : <p>Buy {selectedOption?.symbol || ""} with card</p>}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
